import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { CheckCircle2 } from "lucide-react";

const MAX_QUOTE = 500;

const empty = {
  name: "", role: "", organization: "", quote: "",
  linkedin_url: "", headshot_url: "", submitter_email: "",
};

export default function EndorseForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [inviteLabel, setInviteLabel] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/public/endorsements/invite/${token}`)
      .then((r) => {
        setInviteLabel(r.data.label || "your invite");
        setForm((f) => ({
          ...f,
          name: f.name || r.data.prefill_name || "",
          role: f.role || r.data.prefill_role || "",
          organization: f.organization || r.data.prefill_org || "",
        }));
      })
      .catch((err) => setInviteError(formatApiError(err.response?.data?.detail) || "Invite is no longer valid."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.name.trim()) {
      toast.error("Name and quote are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/endorsements", {
        ...form,
        submitter_email: form.submitter_email || null,
        invite_token: token || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-cream min-h-screen text-navy" data-testid="endorse-thankyou">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 text-center">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
          <div className="overline text-gold-deep mt-6">Submitted</div>
          <h1 className="editorial-h1 text-4xl md:text-5xl text-navy mt-4">Thank you.</h1>
          <div className="gold-rule mx-auto mt-6 mb-8" />
          <p className="text-navy/75 font-light leading-relaxed">
            Your endorsement is in Joseph's review queue. Once approved it will appear on the public endorsements wall.
          </p>
          <button
            onClick={() => navigate("/endorsements")}
            data-testid="endorse-view-wall"
            className="mt-10 border border-navy text-navy px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold"
          >
            View the wall →
          </button>
        </div>
      </div>
    );
  }

  if (token && inviteError) {
    return (
      <div className="bg-cream min-h-screen text-navy" data-testid="endorse-invite-invalid">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-6 md:px-10 py-24 text-center">
          <div className="overline text-gold-deep">Invite</div>
          <h1 className="editorial-h1 text-4xl md:text-5xl text-navy mt-4">Link no longer valid.</h1>
          <div className="gold-rule mx-auto mt-6 mb-8" />
          <p className="text-navy/75 font-light">{inviteError}</p>
          <Link to="/endorse" className="inline-block mt-8 text-xs uppercase tracking-widest underline decoration-gold underline-offset-4 hover:text-navy">
            Leave a public endorsement instead →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen text-navy" data-testid="endorse-form-page">
      <Toaster richColors position="top-right" />
      <SiteHeader />

      <section className="max-w-2xl mx-auto px-6 md:px-10 pt-16 pb-24">
        <div className="overline text-gold-deep mb-4">
          {token && inviteLabel ? `Invited: ${inviteLabel}` : "Open endorsement"}
        </div>
        <h1 className="editorial-h1 text-4xl md:text-5xl text-navy">
          Leave an <span className="italic">endorsement.</span>
        </h1>
        <div className="gold-rule mt-6 mb-10" />
        <p className="text-navy/75 font-light leading-relaxed">
          A short, candid line about working with Joseph — what you'd say to a journalist or LP. Approved
          endorsements show on the public wall with your name, role and organization.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="endorse-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="overline text-navy/60">Your name *</label>
              <input
                required
                data-testid="endorse-input-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline text-navy/60">Role / Title</label>
              <input
                data-testid="endorse-input-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline text-navy/60">Organization</label>
              <input
                data-testid="endorse-input-org"
                value={form.organization}
                onChange={(e) => setForm({ ...form, organization: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="overline text-navy/60">Endorsement * (max {MAX_QUOTE} chars)</label>
              <span className={`text-xs ${form.quote.length > MAX_QUOTE ? "text-destructive" : "text-navy/40"}`}>
                {form.quote.length}/{MAX_QUOTE}
              </span>
            </div>
            <textarea
              required
              rows={5}
              maxLength={MAX_QUOTE}
              data-testid="endorse-input-quote"
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              placeholder="Be specific — what mattered, what surprised you, what you'd tell a peer."
              className="w-full mt-2 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="overline text-navy/60">LinkedIn URL</label>
              <input
                type="url"
                data-testid="endorse-input-linkedin"
                placeholder="https://linkedin.com/in/…"
                value={form.linkedin_url}
                onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline text-navy/60">Headshot URL (optional)</label>
              <input
                type="url"
                data-testid="endorse-input-headshot"
                placeholder="https://…/photo.jpg"
                value={form.headshot_url}
                onChange={(e) => setForm({ ...form, headshot_url: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline text-navy/60">Your email (private)</label>
              <input
                type="email"
                data-testid="endorse-input-email"
                value={form.submitter_email}
                onChange={(e) => setForm({ ...form, submitter_email: e.target.value })}
                className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
              />
              <div className="text-xs text-navy/40 mt-1">Not shown publicly. Used only to confirm authorship.</div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-navy/10">
            <Link to="/endorsements" className="text-xs uppercase tracking-widest text-navy/60 hover:text-navy">
              ← Back to wall
            </Link>
            <button
              type="submit"
              disabled={submitting}
              data-testid="endorse-submit"
              className="bg-navy text-gold px-7 py-3 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit endorsement"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
